import Box from '@mui/material/Box'
import { SxProps } from '@mui/material/styles'

import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { useAuth } from 'src/context/AuthContext'
import { useBillingContext } from 'src/context/BillingContext'
import { useLoadingData } from 'src/context/LoadingContext'
import { useTopbarContext } from 'src/context/TopbarContext'
import BillingExpired from 'src/layout/BillingExpired'
import PreviewPage from 'src/pages/members/Preview'
import DialogBugReporting from 'src/pages/members/ReportBug/DialogBugReport'
import { get } from 'src/utils/config/lodashUtils'
import BackdropLoader from '../Loading/BackdropLoader'
import { NewFilterComponentProps } from '../NewFilterComponent/NewFilterComponent'

interface NavItem {
  type: string
  uid: string
}

interface Route {
  uid: string
  key: string
}

interface PropTypes {
  title: string | React.ReactNode
  children: React.ReactNode
  copyUrl?: boolean
  sx?: SxProps
  bgcolor?: string
  filterProps?: NewFilterComponentProps
  hideFilters?: boolean
  renderPageWrapper?: boolean
  disableAutoLoader?: boolean
}

const Page = (props: PropTypes): JSX.Element => {
  const { renderPageWrapper = true, disableAutoLoader = false } = props
  const { setTitle, setCopyUrl, setFilterProps, setHideFilters } = useTopbarContext()
  const { globalRoutes, orgConfig } = useAuth()
  const { backdropLoading, setBackdropLoading } = useLoadingData()
  const { activeBillingConfig, isBillingConfigExpired, billingLoading } = useBillingContext()

  const setTopbarContextStates = () => {
    if (isBillingConfigExpired) {
      setCopyUrl(false)
      setFilterProps({})
      setHideFilters(true)
    } else {
      setCopyUrl(get(props, 'copyUrl', true))
      setFilterProps(get(props, 'filterProps', {}))
      setHideFilters(get(props, 'hideFilters', false))
    }
  }

  const ref = useRef({})

  useLayoutEffect(() => {
    if (!renderPageWrapper) return

    setTopbarContextStates()
  }, [get(props, 'filterProps', ref.current), renderPageWrapper, isBillingConfigExpired])

  useEffect(() => {
    if (!renderPageWrapper) return
    setTitle(isBillingConfigExpired ? '' : props.title)
  }, [props.title, renderPageWrapper, activeBillingConfig?.activePhase])

  useEffect(() => {
    if (!renderPageWrapper) return

    scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant' as ScrollBehavior
    })

    if (isBillingConfigExpired) return

    if (disableAutoLoader) return

    setBackdropLoading(true)
    setTimeout(() => {
      setBackdropLoading(false)
    }, 2000)
  }, [renderPageWrapper, isBillingConfigExpired, disableAutoLoader])

  useEffect(() => {
    if (!renderPageWrapper) return

    if (backdropLoading) {
      document.body.style.overflowY = 'hidden'
    } else {
      document.body.style.overflowY = ''
    }

    return () => {
      document.body.style.overflowY = ''
    }
  }, [backdropLoading, renderPageWrapper])

  function getAllPreviewPagesPathnames(navConfig: NavItem[]): string[] {
    const routeUidArray: string[] = []

    navConfig?.forEach((navItem: NavItem) => {
      if (navItem.type === 'preview') {
        routeUidArray.push(navItem.uid)
      }
    })

    const routes = globalRoutes.filter((route: Route) => routeUidArray.includes(route.uid)).map((route: Route) => route.key)

    return routes
  }

  const isPreviewPagePath = (pathname: string, previewPagesPathNames: string[]): boolean => {
    if (previewPagesPathNames.includes(pathname)) {
      return true
    }
    const firstSegment = pathname.split('/')[0]
    return previewPagesPathNames.includes(firstSegment)
  }

  const isPreviewPage = React.useMemo(() => {
    const previewPagesPathNames = getAllPreviewPagesPathnames(orgConfig.navConfig)
    const pathnameUri = location.pathname.substring(1)

    if (isPreviewPagePath(pathnameUri, previewPagesPathNames)) {
      return true
    } else {
      return false
    }
  }, [location.pathname, orgConfig.navConfig])

  if (isPreviewPage) {
    return <PreviewPage modulePageName={props.title} />
  }

  return (
    <>
      <div>
        {renderPageWrapper ? (
          <>
            <Box
              height="100%"
              sx={{
                position: 'relative'
              }}
              bgcolor="transparent">
              <BackdropLoader />
            </Box>
            {isBillingConfigExpired ? (
              <BillingExpired />
            ) : (
              <Box
                height="100%"
                sx={{
                  ...get(props, 'sx', {}),
                  position: 'relative'
                }}
                bgcolor={get(props, 'bgcolor', '#FFFFFF')}>
                {billingLoading ? <BackdropLoader /> : props.children}
              </Box>
            )}
          </>
        ) : (
          props.children
        )}
      </div>

      <DialogBugReporting />
    </>
  )
}

export default Page
